import React, { useEffect, useId, useRef, useState } from 'react';
import { Dialog, Field, inputClass } from '../ds/Dialog';
import { Button, FileTile } from '../ds';
import { InstrumentPicker, instrumentFields, pickerValue } from './InstrumentPicker';
import { ErrorNote } from './ErrorNote';
import { audioFileProblem } from '../../hooks/useAudioFileInput';
import { guessInstrument, suggestClipName, OTHER } from '../../utils/instruments';

const EMPTY = { category: null, type: null, other: '' };
const MAX_NOTE_CHARS = 1000;

const sizeText = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Add a clip to the project: the file, its instrument (guessed from the file name), a name, and an
 * optional goal. "Add and analyze" hands them to onAdd, which closes the dialog and analyzes V1 in
 * the new clip's chat. If that fails, it opens again with `retry` ({ fields, error }): what they
 * chose, filled back in, and why it failed.
 */
export const AddClipDialog = ({ open, onClose, projectName, initialFile = null, retry = null, onAdd }) => {
  const [file, setFile] = useState(null);
  const [instrument, setInstrument] = useState(EMPTY);
  const [guessed, setGuessed] = useState(false);
  const [name, setName] = useState(null); // null until the user types their own
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const nameId = useId();
  const noteId = useId();

  const chooseFile = (picked) => {
    const problem = audioFileProblem(picked);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setFile(picked);
    const guess = guessInstrument(picked.name);
    // Only guess while the user hasn't picked an instrument themselves
    if (guess && (guessed || !instrument.category)) {
      setInstrument({ ...guess, other: '' });
      setGuessed(true);
    }
  };

  // Start fresh each time it opens, with any file that was dropped to open it
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setInstrument(EMPTY);
    setGuessed(false);
    setName(null);
    setNote('');
    setBusy(false);
    setError(null);
    if (retry) {
      const { fields } = retry;
      setFile(fields.file);
      setInstrument(pickerValue({ category: fields.category, instrument_type: fields.instrumentType }));
      setName(fields.name);
      setNote(fields.note || '');
      setError(retry.error);
      return;
    }
    if (initialFile) {
      const problem = audioFileProblem(initialFile);
      if (problem) setError(problem);
      else {
        setFile(initialFile);
        const guess = guessInstrument(initialFile.name);
        if (guess) {
          setInstrument({ ...guess, other: '' });
          setGuessed(true);
        }
      }
    }
  }, [open, initialFile, retry]);

  const fields = instrumentFields(instrument);
  const suggested = instrument.category === OTHER ? (instrument.other || '').trim() : suggestClipName(instrument);
  const clipName = name ?? suggested;
  const ready = Boolean(file && fields && clipName.trim()) && !busy;

  const submit = async (event) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd({ file, name: clipName.trim(), category: fields.category, instrumentType: fields.instrumentType, note: note.trim() });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Add a clip" subtitle={projectName ? `to ${projectName}` : undefined} busy={busy}>
      <form onSubmit={submit} className="flex flex-col gap-space-6">
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          data-testid="add-clip-file-input"
          onChange={(event) => {
            if (event.target.files?.[0]) chooseFile(event.target.files[0]);
            event.target.value = '';
          }}
        />
        {file ? (
          <div className="flex items-center gap-space-3 p-space-3 rounded-radius-md bg-base border border-line">
            <FileTile />
            <span className="flex-1 min-w-0 flex flex-col gap-0.5">
              <span className="truncate font-mono text-data-sm text-ink">{file.name}</span>
              <span className="text-caption text-muted">{sizeText(file.size)}</span>
            </span>
            <Button variant="text" onClick={() => fileInputRef.current?.click()} disabled={busy}>Change file</Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-space-3 p-space-4 rounded-radius-md bg-base border border-dashed border-line">
            <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>Choose an audio file</Button>
            <span className="text-caption text-muted">WAV, MP3 or FLAC, up to 10 minutes and 100 MB</span>
          </div>
        )}

        {/* Locked while it hands over */}
        <fieldset disabled={busy} className="m-0 p-0 border-0 min-w-0 flex flex-col gap-space-6 disabled:opacity-60">
          <InstrumentPicker
            value={instrument}
            guessed={guessed}
            onChange={(next) => {
              setInstrument(next);
              setGuessed(false);
            }}
          />

          <Field id={nameId} label="Clip name">
            <input id={nameId} type="text" maxLength={100} value={clipName} onChange={(event) => setName(event.target.value)} className={inputClass} />
          </Field>

          <Field id={noteId} label="What are you going for?" hint="Optional">
            <textarea
              id={noteId}
              rows={2}
              maxLength={MAX_NOTE_CHARS}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="e.g. I want it punchy but not boomy"
              className={`${inputClass} py-space-3 resize-y`}
            />
          </Field>
        </fieldset>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex flex-wrap justify-end gap-space-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={!ready}>{busy ? 'Analyzing…' : 'Add and analyze'}</Button>
        </div>
      </form>
    </Dialog>
  );
};

/**
 * Rename or retag a clip, or delete it (after a confirmation step). Retagging changes how later
 * versions are scored, not the scores it already has.
 */
export const EditClipDialog = ({ open, onClose, clip, onSave, onDelete }) => {
  const [instrument, setInstrument] = useState(EMPTY);
  const [name, setName] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const nameId = useId();

  useEffect(() => {
    if (!open || !clip) return;
    setInstrument(pickerValue(clip));
    setName(clip.name);
    setConfirmingDelete(false);
    setBusy(false);
    setError(null);
  }, [open, clip]);

  if (!clip) return null;
  const fields = instrumentFields(instrument);
  const versions = clip.latest_version || 0;

  const run = async (action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Edit clip" subtitle={clip.name} busy={busy}>
      {confirmingDelete ? (
        <div className="flex flex-col gap-space-6">
          <p className="m-0 text-body text-ink-soft">
            Delete {clip.name} and its {versions === 1 ? 'version' : `${versions} versions`} and chat? This can't be undone.
          </p>
          {error && <ErrorNote>{error}</ErrorNote>}
          <div className="flex flex-wrap justify-end gap-space-3">
            <Button variant="secondary" onClick={() => setConfirmingDelete(false)} disabled={busy}>Keep it</Button>
            <Button onClick={() => run(onDelete)} disabled={busy}>{busy ? 'Deleting…' : 'Delete clip'}</Button>
          </div>
        </div>
      ) : (
        <form
          className="flex flex-col gap-space-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (fields && name.trim()) {
              run(() => onSave({ name: name.trim(), category: fields.category, instrument_type: fields.instrumentType }));
            }
          }}
        >
          <Field id={nameId} label="Clip name">
            <input id={nameId} type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} className={inputClass} />
          </Field>
          <InstrumentPicker value={instrument} onChange={setInstrument} />
          {error && <ErrorNote>{error}</ErrorNote>}
          <div className="flex flex-wrap items-center justify-between gap-space-3">
            <Button variant="text" onClick={() => setConfirmingDelete(true)} disabled={busy}>Delete clip</Button>
            <div className="flex flex-wrap gap-space-3">
              <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={busy || !fields || !name.trim()}>{busy ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </form>
      )}
    </Dialog>
  );
};
